import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import {
  collapseToRanges,
  prepareCodesForSubmit,
  RangeTooLargeError,
} from "helpers/codeRanges";
import useDocumentTitle from "hooks/useDocumentTitle";
import RuleHistory from "pages/PayerRules/components/RuleHistory";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useApplyUnitCapPropagationMutation,
  useGetUnitCapByIdQuery,
  useLazyGetUnitCapPropagationPreviewQuery,
  useUpdateUnitCapMutation,
} from "store/queries/unitCap";

import {
  IUnitCapFormData,
  INITIAL_UNIT_CAP_FORM_DATA,
  UnitCapForm,
} from "./components/UnitCapForm";
import { usePropagateAfterEdit } from "./components/usePropagateAfterEdit";

export function EditUnitCapPage() {
  useDocumentTitle("Edit Unit Cap Rule");
  const navigate = useNavigate();
  const { id } = useParams();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const { data, isLoading: isFetching } = useGetUnitCapByIdQuery(id ?? "");
  const [updateMutation, { isLoading: isUpdating }] =
    useUpdateUnitCapMutation();
  const [formData, setFormData] = useState<IUnitCapFormData>(
    INITIAL_UNIT_CAP_FORM_DATA
  );

  const { openForId, modal: propagateModal } = usePropagateAfterEdit({
    useLazyPreview: useLazyGetUnitCapPropagationPreviewQuery as any,
    useApplyPropagation: useApplyUnitCapPropagationMutation as any,
    onDone: () => navigate("/payer-rules"),
  });

  useEffect(() => {
    const ruleset = data?.data;
    if (!ruleset) return;
    setFormData({
      maxUnitsPerCode: ruleset.maxUnitsPerCode,
      providerId: ruleset.providerId,
      payerId: ruleset.payerId,
      cptCodes: collapseToRanges(ruleset.cptCodes ?? []),
      groupName: ruleset.groupName ?? "",
      contractLanguage: ruleset.contractLanguage ?? "",
      isDefault: ruleset.isDefault,
    });
  }, [data]);

  const handleSubmit = async () => {
    if (!id) return;
    if (!formData.isDefault && (!formData.providerId || !formData.payerId))
      return;
    let cptCodes: string[];
    try {
      cptCodes = prepareCodesForSubmit(formData.cptCodes);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Range too large",
        detail:
          err instanceof RangeTooLargeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    setIsLoading(true);
    try {
      await updateMutation({
        id,
        maxUnitsPerCode: formData.maxUnitsPerCode,
        cptCodes,
        groupName: formData.groupName.trim() || null,
        contractLanguage: formData.contractLanguage.trim() || null,
        isDefault: formData.isDefault,
        providerId: formData.isDefault ? null : formData.providerId,
        payerId: formData.isDefault ? null : formData.payerId,
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Updated!",
        detail: "Unit Cap rule updated successfully.",
      });
      if (formData.isDefault) {
        await openForId(id);
      } else {
        navigate("/payer-rules");
      }
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: error?.data?.message || "Failed to update unit cap rule",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) return <LoadingSpinner />;

  return (
    <>
      <UnitCapForm
        title="Edit Unit Cap Rule"
        submitLabel="Save Changes"
        formData={formData}
        onChange={setFormData}
        onSubmit={handleSubmit}
        isSubmitting={isUpdating}
        scopeLocked
      />
      {propagateModal}
      <RuleHistory histories={data?.data.history} />
    </>
  );
}
